// Built-in copies of the help guides, keyed by topic id.
//
// The AI service is the source of truth: config/portal-flows.json in
// dcdial-nodejs-ai-api, which /ai/portal/suggestions serves. But the service
// only started sending description and steps recently, and deploys on its own
// schedule — so until every environment's service sends them, these copies
// keep the Help guides tab able to open a guide instead of only linking to a
// screen. A topic that arrives with its own steps ignores its entry here, so
// once the service catches up this file is dead weight that can be deleted.
//
// If a guide is renamed or a screen moves, config/portal-flows.json is where
// it changes — update this file in the same PR or the fallback drifts.

import { AiTopic } from "./aiApi";

export interface GuideContent {
  description?: string;
  steps: string[];
}

const GUIDE_FALLBACKS: Record<string, GuideContent> = {
  "make-a-payment": {
    description: "Pay an amount now with a card or bank account.",
    steps: [
      "Open Make a payment.",
      "Enter the amount you want to pay.",
      "Choose a saved payment method, or add a new card or bank account.",
      "Review the amount and the method, then confirm.",
    ],
  },
  "view-payment-history": {
    description:
      "See payments that went through, payments that failed, and download receipts.",
    steps: [
      "Open Payments from the sidebar.",
      "The list shows each payment with its date, amount and status.",
      "Select a payment to see its details or download the receipt.",
    ],
  },
  "retry-failed-payment": {
    description: "Try a payment again after it was declined.",
    steps: [
      "Open Payments and find the payment marked failed.",
      "Select it to see why it was declined.",
      "Choose Retry, then confirm the payment method to use.",
      "If the same method failed for a card reason, add a different one first.",
    ],
  },
  "add-payment-method": {
    description: "Save a new card or bank account to pay with.",
    steps: [
      "Open Payment Methods from the sidebar.",
      "Choose to add a card, or to link a bank account.",
      "Enter the details and save.",
    ],
  },
  "update-payment-method": {
    description: "Change the card on file, for example after it expires.",
    steps: [
      "Open Payment Methods from the sidebar.",
      "Select the card or account you want to change.",
      "Update the details and save.",
      "If it is used by a payment plan, the plan will use the updated details from its next payment.",
    ],
  },
  "view-payment-plan": {
    description:
      "See an active plan or subscription, its instalments and the next payment date.",
    steps: [
      "Open Subscriptions & Plans from the sidebar.",
      "Select a plan to see its schedule.",
      "The schedule lists each instalment with its date, amount and status.",
    ],
  },
  "action-center": {
    description:
      "Anything waiting on you — an unpaid balance, a failed payment, a document to sign.",
    steps: [
      "Open Action Center from the sidebar.",
      "Each item says what is needed and links to the screen that handles it.",
    ],
  },
  "view-documents": {
    description: "Statements, agreements and other documents shared with you.",
    steps: [
      "Open Documents from the sidebar.",
      "Select a document to view or download it.",
    ],
  },
  "update-my-details": {
    description: "Change the phone number or email address on your account.",
    steps: [
      "Open your profile.",
      "Edit the phone number or email address.",
      "Confirm the change with the verification code sent to you.",
    ],
  },
  "contact-support": {
    description: "Reach the team when the portal cannot resolve something.",
    steps: [
      "Open Support & Help.",
      "Use the contact details there to reach the team.",
    ],
  },
};

/**
 * The guide to show for a topic, service copy first.
 *
 * Null means there is nothing to expand — an unknown topic with no steps of
 * its own — and the row should fall back to opening its screen directly.
 */
export function guideFor(topic: AiTopic): GuideContent | null {
  if (topic.steps?.length) {
    return { description: topic.description, steps: topic.steps };
  }
  return GUIDE_FALLBACKS[topic.topic] ?? null;
}
